import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Star, ChevronRight, Check, BarChart3,
  Coffee, UtensilsCrossed, Pill, Hotel, Apple, Shirt, Building2, Pizza,
  Croissant, QrCode, Smartphone, MessageCircle, TrendingUp, ArrowRight,
  Users, Camera, ShoppingCart, Dumbbell, Scissors, Dog, Sandwich, Phone,
  Bell, MessageSquare, ShieldCheck, Megaphone, Sparkles,
  UserPlus, QrCode as QrCodeIcon, AlertCircle, Flame, Share2
} from 'lucide-react';
import { RoiCalculator } from '@/components/RoiCalculator';
import { PublicFaq } from '@/components/PublicFaq';
import { fetchShelfIds } from '@/constants/plans';

const API = process.env.REACT_APP_BACKEND_URL;

/* ──── Badge Fixo: Social Proof (sem alternancia, para nao distrair durante a leitura/video) ──── */
const RollingBadge = () => {
  const [stats, setStats] = useState({
    companies_total: 500,
    reviews_total: 25000,
  });

  useEffect(() => {
    fetch(`${API}/api/public/landing-stats`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setStats(d); })
      .catch(() => {});
  }, []);

  const fmt = (n) => n >= 1000 ? `${Math.floor(n / 1000)}k+` : `${n}+`;

  return (
    <div className="mb-6 h-8 w-full max-w-full overflow-hidden flex items-center" data-testid="rolling-badge-wrapper">
      <div
        className="flex items-center gap-2 bg-white/[0.06] border border-[#C5A059]/25 backdrop-blur-sm rounded-full px-4 h-8 max-w-full min-w-0 overflow-hidden"
        data-testid="rolling-badge"
      >
        <Star className="w-4 h-4 text-[#C5A059] fill-[#C5A059] shrink-0" />
        <span className="text-xs sm:text-sm font-medium tracking-wide text-slate-200 truncate whitespace-nowrap min-w-0">
          <strong className="text-white">{fmt(stats.reviews_total)}</strong> avaliações interceptadas
          <span className="hidden sm:inline"> · </span>
          <span className="hidden sm:inline"><strong className="text-white">{fmt(stats.companies_total)}</strong> empresas protegidas</span>
        </span>
      </div>
    </div>
  );
};

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72ee028-442b-44ea-ad3d-be1abd62a5cf/artifacts/zdjtc3rc_Logo_Falo_com_o_dono-removebg-preview.png";

const IMAGES = {
  padaria: "https://customer-assets.emergentagent.com/job_a72ee028-442b-44ea-ad3d-be1abd62a5cf/artifacts/6wywzwmh_bakery-with-display-pastries-pastries.jpg",
  restaurant: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=800&q=80",
  cafe: "https://customer-assets.emergentagent.com/job_a72ee028-442b-44ea-ad3d-be1abd62a5cf/artifacts/jcvz69l5_close-up-delicious-coffee-cup%20%281%29.jpg",
  hotel: "https://images.unsplash.com/photo-1742844552264-71e01c8dd7c0?auto=format&fit=crop&w=800&q=80",
  dental: "https://images.pexels.com/photos/305567/pexels-photo-305567.jpeg?auto=compress&cs=tinysrgb&w=800",
  fashion: "https://images.unsplash.com/photo-1570857502809-08184874388e?auto=format&fit=crop&w=800&q=80",
};

const GoldStar = ({ size = 'w-5 h-5' }) => (
  <Star className={`${size} text-[#C5A059] fill-[#C5A059]`} />
);

/* ──── CSS Mockup: Dashboard BI ──── */
const DashboardMockup = () => {
  const bars = [
    { label: 'Demora no Atendimento', pct: 100, count: 12 },
    { label: 'Qualidade do Pedido', pct: 66, count: 8 },
    { label: 'Limpeza/Higiene', pct: 33, count: 4 },
    { label: 'Temperatura do Prato', pct: 25, count: 3 },
  ];
  return (
    <div className="bg-slate-900/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-5 shadow-2xl shadow-black/30 max-w-sm mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-4 h-4 text-[#C5A059]" />
        <p className="text-xs font-bold text-white">Top Reclamacoes</p>
      </div>
      <div className="space-y-3">
        {bars.map(b => (
          <div key={b.label}>
            <div className="flex justify-between mb-1">
              <span className="text-[10px] text-white/70">{b.label}</span>
              <span className="text-[10px] font-bold text-red-400">{b.count}x</span>
            </div>
            <div className="h-2 rounded-full bg-slate-800">
              <div className="h-full rounded-full bg-gradient-to-r from-red-500 to-orange-500 transition-all duration-700"
                style={{ width: `${b.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ──── CSS Mockup: WhatsApp Alert ──── */
const WhatsAppMockup = () => (
  <div className="bg-[#1a2e1a] rounded-2xl border border-emerald-800/50 p-4 shadow-2xl shadow-black/30 max-w-xs mx-auto">
    <div className="flex items-center gap-2 mb-3">
      <MessageSquare className="w-4 h-4 text-emerald-400" />
      <p className="text-[10px] font-bold text-emerald-400">ALERTA WHATSAPP</p>
    </div>
    <div className="bg-emerald-900/40 rounded-xl p-3">
      <p className="text-[11px] text-emerald-100 font-bold leading-relaxed">
        *ALERTA DE CRISE - Padaria do Joao*
      </p>
      <p className="text-[10px] text-emerald-200/70 mt-1">
        Cliente: Maria Silva<br/>
        Nota: 1.7/5<br/>
        "Pao borrachudo e cafe morno"
      </p>
      <p className="text-[10px] text-yellow-300/90 mt-2 font-semibold">
        Problemas: Frescor/Qualidade, Temperatura
      </p>
    </div>
  </div>
);

/* ──── Hero Video: YouTube Shorts embed (vertical 9:16) ──── */
const HeroVideo = () => (
  <div
    className="relative mx-auto w-full max-w-[320px] lg:max-w-[360px]"
    data-testid="hero-video"
  >
    {/* Moldura dourada com glow premium */}
    <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-black/50 border-2 border-[#C5A059]/30 bg-black">
      <div className="relative w-full" style={{ paddingTop: '177.78%' /* 9:16 Shorts */ }}>
        <iframe
          className="absolute inset-0 w-full h-full"
          src="https://www.youtube-nocookie.com/embed/dU08E0f0dms?rel=0&modestbranding=1&playsinline=1"
          title="Falo Com O Dono - Apresentação em 1:26"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
        />
      </div>
    </div>
    {/* Glow dourado decorativo */}
    <div className="absolute -inset-4 bg-[#C5A059]/10 rounded-[2.5rem] blur-2xl -z-10" aria-hidden="true" />
  </div>
);

const LandingPage = () => {
  // Captura cupom da URL para repassar nos links de checkout (campanhas remarketing).
  const [searchParams] = useSearchParams();
  const urlCoupon = searchParams.get('cupom') || searchParams.get('coupon') || '';
  const checkoutQS = urlCoupon ? `?cupom=${encodeURIComponent(urlCoupon)}` : '';

  // Prateleira de planos (flag visivel_na_vitrine, editavel no /master)
  const [shelfIds, setShelfIds] = useState(['enterprise', 'elite', 'five_stars']);
  useEffect(() => { fetchShelfIds().then(setShelfIds); }, []);

  const segments = [
    { icon: <Croissant className="w-6 h-6" />, name: 'Padarias' },
    { icon: <Coffee className="w-6 h-6" />, name: 'Cafeterias' },
    { icon: <UtensilsCrossed className="w-6 h-6" />, name: 'Restaurantes' },
    { icon: <Pizza className="w-6 h-6" />, name: 'Pizzarias' },
    { icon: <Pill className="w-6 h-6" />, name: 'Farmacias' },
    { icon: <ShoppingCart className="w-6 h-6" />, name: 'Supermercados' },
    { icon: <Sandwich className="w-6 h-6" />, name: 'Lanchonetes' },
    { icon: <Dumbbell className="w-6 h-6" />, name: 'Academias' },
    { icon: <Dog className="w-6 h-6" />, name: 'Pet Shops' },
    { icon: <Scissors className="w-6 h-6" />, name: 'Saloes de Beleza' },
    { icon: <Hotel className="w-6 h-6" />, name: 'Hoteis' },
    { icon: <Shirt className="w-6 h-6" />, name: 'Lojas' },
    { icon: <Apple className="w-6 h-6" />, name: 'Hortifrutis' },
    { icon: <Building2 className="w-6 h-6" />, name: 'Clínicas' },
    { icon: <Users className="w-6 h-6" />, name: 'Empresas' },
  ];

  const allPlans = [
    {
      name: 'Básico',
      price: '29,90',
      planId: 'basico',
      seal: 'Ideal para Pequenos Negócios',
      upgrade: 'Tudo do Free e mais...',
      features: ['Até 15 avaliações/mes', 'Dashboard Completo', 'Respostas via WhatsApp e E-mail', 'QR Code + Placas para Balcão', '1 WhatsApp para alertas'],
      googleFeature: 'Alavanque sua Nota no Google Maps',
    },
    {
      name: 'Pro',
      price: '39,90',
      planId: 'pro',
      seal: 'Sua Marca em Destaque',
      upgrade: 'Tudo do Básico e mais...',
      features: ['Até 30 avaliações/mes', 'Sua Logo Própria na Placa e no Sistema', 'PDF A4 para Impressão', 'Respostas via WhatsApp e E-mail', '2 WhatsApp para alertas'],
      googleFeature: 'Alavanque sua Nota no Google Maps',
    },
    {
      name: 'Empresarial',
      price: '59,90',
      planId: 'enterprise',
      best: true,
      seal: 'Mais Popular',
      upgrade: '30 dias grátis para testar',
      features: ['Até 60 avaliações/mes', 'Relatórios em Excel para gestão profissional', 'Suporte Prioritário', 'Controle de Ocultar Avaliações', '3 WhatsApp para alertas'],
      googleFeature: 'Alavanque sua Nota no Google Maps',
    },
    {
      name: 'Elite',
      price: '99,90',
      planId: 'elite',
      seal: 'Controle Total',
      upgrade: 'Tudo do Empresarial e mais...',
      features: ['Até 100 avaliações/mes', 'Alerta de Crise Instantâneo', 'Notificação imediata de notas baixas', 'Controle de Ocultar Avaliações', '4 WhatsApp para alertas'],
      googleFeature: 'Alavanque sua Nota no Google Maps',
    },
    {
      name: '5 Estrelas',
      price: '149,90',
      planId: 'five_stars',
      complete: true,
      seal: 'Experiência Completa',
      upgrade: 'Tudo do Elite e mais...',
      features: ['Avaliações ILIMITADAS', 'Suporte VIP via WhatsApp', 'Controle de Ocultar Avaliações', 'WhatsApp ILIMITADOS'],
      googleFeature: 'Alavanque sua Nota no Google Maps',
    },
    {
      name: 'Free',
      price: '0',
      planId: 'free',
      isFree: true,
      seal: 'Gratuito por 60 dias',
      features: ['Até 10 avaliações/mes', 'Dashboard Completo', 'Respostas via E-mail', 'QR Code + Placas para Balcão', '1 WhatsApp para alertas'],
      googleFeature: 'Alavanque sua Nota no Google Maps',
      freeBadge: true,
    },
  ];

  const plans = allPlans.filter(p => !p.planId || shelfIds.includes(p.planId));

  const testimonials = [
    { name: 'Ricardo', role: 'Padaria Massa Pura', text: 'Um cliente avaliou o banheiro como nota 2. O alerta tocou no meu WhatsApp, mandei limpar na hora e ele saiu agradecido. O sistema se pagou em uma única mesa.', tag: 'Recuperação' },
    { name: 'Ana', role: 'Cantina di Napoli', text: 'Antes eu só sabia dos problemas pelo Google. Agora eu resolvo na hora. E a paz de espirito que todo dono de restaurante precisa.', tag: 'Agilidade' },
    { name: 'Marcos', role: 'Hamburgueria Prime', text: 'Minha nota no Google subiu 1 ponto em apenas dois meses usando o QR Code nas mesas.', tag: 'Resultado' },
  ];

  const scrollToPlans = () => {
    document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="landing-page">

      {/* ==================== HEADER ==================== */}
      <header className="bg-[#0A2647] text-white sticky top-0 z-50 border-b border-white/5" data-testid="header">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 shrink-0">
            <img src={LOGO_URL} alt="Logo" className="h-14 sm:h-16 w-auto object-contain" data-testid="header-logo" />
            <span className="font-bold text-base sm:text-xl hidden sm:block" style={{ fontFamily: 'Montserrat, sans-serif' }}>Falo Com O Dono<sup className="text-[10px] sm:text-xs text-[#C5A059] font-bold ml-0.5">™</sup></span>
          </div>
          <nav className="flex items-center gap-1 sm:gap-2" data-testid="desktop-nav">
            <Link to="/feira" className="hidden sm:inline-flex" data-testid="nav-feira">
              <Button className="relative bg-gradient-to-r from-[#C5A059] to-[#b8933f] hover:from-[#d4af6a] hover:to-[#C5A059] text-[#0A2647] font-bold text-sm h-10 px-5 rounded-lg shadow-lg shadow-[#C5A059]/30 ring-2 ring-[#C5A059]/40 animate-pulse hover:animate-none">
                ⚡ Implantar Agora
              </Button>
            </Link>
            <Link to="/register" data-testid="nav-register">
              <Button variant="outline" className="border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 font-semibold text-[10px] sm:text-sm h-7 sm:h-9 px-2 sm:px-4 rounded-md sm:rounded-lg">
                Cadastro
              </Button>
            </Link>
            <Link to="/login?view=owner" data-testid="nav-owner">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 font-medium text-[10px] sm:text-sm h-7 sm:h-9 px-2 sm:px-4 rounded-md sm:rounded-lg">
                <span className="sm:hidden">Dono</span>
                <span className="hidden sm:inline">Area do Dono</span>
              </Button>
            </Link>
            <Link to="/afiliados/login" data-testid="nav-affiliate">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 font-medium text-[10px] sm:text-sm h-7 sm:h-9 px-2 sm:px-4 rounded-md sm:rounded-lg">
                <span className="sm:hidden">Afiliado</span>
                <span className="hidden sm:inline">Area do Afiliado</span>
              </Button>
            </Link>
            <Link to="/parceiro/acesso" data-testid="nav-partner">
              <Button variant="outline" className="border-[#C5A059]/30 text-[#C5A059] hover:bg-[#C5A059]/10 font-medium text-[10px] sm:text-sm h-7 sm:h-9 px-2 sm:px-4 rounded-md sm:rounded-lg">
                <span className="sm:hidden">Parceiro</span>
                <span className="hidden sm:inline">Area do Parceiro</span>
              </Button>
            </Link>
            <Link to="/master" data-testid="nav-master">
              <Button variant="outline" className="border-white/10 text-slate-400 hover:bg-white/10 font-medium text-[10px] sm:text-sm h-7 sm:h-9 px-2 sm:px-4 rounded-md sm:rounded-lg">
                <span className="sm:hidden">Master</span>
                <span className="hidden sm:inline">Gestor Master</span>
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* ══════════════════ AIDA 1: ATENCAO - Hero ══════════════════ */}
      <section className="relative bg-[#0A2647] overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-5%] w-[600px] h-[600px] bg-[#C5A059]/[0.04] rounded-full blur-[150px]" />
          <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-[#C5A059]/[0.06] rounded-full blur-[120px]" />
          <div className="absolute inset-0 opacity-[0.025]" style={{
            backgroundImage: 'linear-gradient(rgba(197,160,89,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(197,160,89,0.6) 1px, transparent 1px)',
            backgroundSize: '80px 80px'
          }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          {/* Desktop: 2 colunas (texto + video) · Mobile: empilhado (Titulo > Sub > Video > CTA) */}
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center pt-10 pb-16 lg:pt-20 lg:pb-24">

            {/* Coluna de texto */}
            <div className="text-left order-1 lg:order-1 flex flex-col">
              {/* Botão grande mobile — Implantar Agora */}
              <Link to="/feira" className="block sm:hidden mb-5" data-testid="hero-feira-mobile">
                <Button className="w-full bg-gradient-to-r from-[#C5A059] to-[#b8933f] hover:from-[#d4af6a] hover:to-[#C5A059] text-[#0A2647] font-bold text-base h-13 py-3.5 rounded-xl shadow-lg shadow-[#C5A059]/30 ring-2 ring-[#C5A059]/40">
                  ⚡ Implantar Agora
                </Button>
              </Link>
              <RollingBadge />

              <h1
                className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-[1.08] tracking-tight text-white mb-5 lg:mb-6"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
                data-testid="hero-headline"
              >
                Dono de restaurante, padaria ou farmácia: Quantos clientes você perdeu hoje{' '}
                <span className="text-[#C5A059]">sem saber o motivo?</span>
              </h1>

              {/* Video entre H1 e Sub/CTA no mobile */}
              <div className="block lg:hidden mb-6" data-testid="hero-video-mobile">
                <HeroVideo />
              </div>

              <p className="text-base sm:text-lg text-slate-300/90 leading-relaxed max-w-xl mb-8 lg:mb-10" data-testid="hero-subheadline">
                O seu cliente insatisfeito não fala mais com o gerente. Ele vai embora calado e desconta a raiva na internet <strong className="text-white">destruindo sua nota no Google Maps</strong>. Assuma o controle agora.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <Link to="/feira" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto relative bg-[#C5A059] hover:bg-[#d4af61] text-[#0A2647] font-bold text-base px-8 h-14 rounded-xl gap-2 transition-all duration-300 hover:shadow-[0_0_30px_rgba(197,160,89,0.4)] active:scale-[0.98]"
                    data-testid="hero-cta"
                  >
                    ⚡ Implantar Agora
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <button onClick={scrollToPlans} className="text-sm text-slate-400 hover:text-[#C5A059] transition-colors flex items-center gap-1 mt-2 sm:mt-4" data-testid="hero-plans-link">
                  Ou veja os planos e preços <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              <div className="mt-8 lg:mt-10 flex flex-wrap items-center gap-5 text-xs sm:text-sm text-slate-400">
                <div className="flex items-center gap-1.5">
                  <div className="flex -space-x-0.5">
                    {[...Array(5)].map((_, i) => <GoldStar key={`hero-star-${i}`} size="w-3.5 h-3.5" />)}
                  </div>
                  <span className="ml-1">5 estrelas</span>
                </div>
                <div className="w-px h-4 bg-slate-600" />
                <span>Setup em 5 minutos</span>
                <div className="w-px h-4 bg-slate-600" />
                <span>Sem cartão de crédito</span>
              </div>
            </div>

            {/* Coluna do video - Desktop */}
            <div className="relative hidden lg:block order-2" data-testid="hero-video-desktop">
              <HeroVideo />
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SOCIAL PROOF BAR ==================== */}
      <section className="bg-[#0A2647] border-t border-white/5 py-5" data-testid="social-proof-bar">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap justify-center gap-8 sm:gap-12 text-center">
            <div>
              <p className="text-2xl sm:text-3xl font-extrabold text-[#C5A059]" style={{ fontFamily: 'Montserrat, sans-serif' }}>500+</p>
              <p className="text-xs text-slate-400 mt-0.5">Empresas Ativas</p>
            </div>
            <div className="w-px bg-white/10 hidden sm:block" />
            <div>
              <p className="text-2xl sm:text-3xl font-extrabold text-[#C5A059]" style={{ fontFamily: 'Montserrat, sans-serif' }}>25.000+</p>
              <p className="text-xs text-slate-400 mt-0.5">Avaliações Recebidas</p>
            </div>
            <div className="w-px bg-white/10 hidden sm:block" />
            <div>
              <p className="text-2xl sm:text-3xl font-extrabold text-[#C5A059]" style={{ fontFamily: 'Montserrat, sans-serif' }}>4.9</p>
              <p className="text-xs text-slate-400 mt-0.5">Nota Media</p>
            </div>
            <div className="w-px bg-white/10 hidden sm:block" />
            <div>
              <p className="text-2xl sm:text-3xl font-extrabold text-[#C5A059]" style={{ fontFamily: 'Montserrat, sans-serif' }}>5min</p>
              <p className="text-xs text-slate-400 mt-0.5">Setup Completo</p>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════ AIDA 2: DESEJO - Escudo e Megafone ══════════════════ */}
      <section className="relative py-20 lg:py-28 overflow-hidden" data-testid="shield-megaphone-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A2647] via-[#0e3260] to-[#0A2647]" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-[#C5A059]/[0.04] rounded-full blur-[140px]" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14 lg:mb-16">
            <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/20 rounded-full px-4 py-1.5 mb-5">
              <Sparkles className="w-3.5 h-3.5 text-[#C5A059]" />
              <span className="text-xs text-[#C5A059] font-bold uppercase tracking-wider">A Virada de Jogo</span>
            </div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-[1.1] tracking-tight mb-5"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
              data-testid="shield-megaphone-title"
            >
              A Estratégia Invisível: <span className="text-[#C5A059]">O Escudo e o Megafone</span> do seu Negócio
            </h2>
            <p className="text-base sm:text-lg text-slate-300/80 max-w-2xl mx-auto leading-relaxed">
              Duas engrenagens trabalhando em silêncio, 24 horas por dia, para proteger sua reputação e multiplicar sua visibilidade.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">

            {/* Bloco 1: O Escudo */}
            <div
              className="group relative bg-gradient-to-br from-white/[0.06] to-white/[0.02] backdrop-blur-sm rounded-3xl p-8 lg:p-10 border border-white/10 hover:border-red-400/30 transition-all duration-500"
              data-testid="shield-block"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 bg-red-500/10 border border-red-400/30 rounded-2xl flex items-center justify-center">
                  <ShieldCheck className="w-7 h-7 text-red-300" />
                </div>
                <div>
                  <p className="text-xs font-bold text-red-300/80 uppercase tracking-[0.2em]">Defesa</p>
                  <h3 className="text-2xl font-extrabold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>O Escudo</h3>
                </div>
              </div>

              <p className="text-base text-slate-200/90 leading-relaxed mb-6">
                <strong className="text-white">Reclamações caem direto no seu celular.</strong> Você resolve o problema em sigilo nos bastidores, antes que ele vire uma reclamação pública.
              </p>

              <div className="pt-6 border-t border-white/10">
                <WhatsAppMockup />
                <p className="text-[11px] text-white/40 text-center mt-3">
                  Alerta instantâneo no seu WhatsApp
                </p>
              </div>
            </div>

            {/* Bloco 2: O Megafone */}
            <div
              className="group relative bg-gradient-to-br from-white/[0.06] to-white/[0.02] backdrop-blur-sm rounded-3xl p-8 lg:p-10 border border-white/10 hover:border-[#C5A059]/40 transition-all duration-500"
              data-testid="megaphone-block"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-2xl flex items-center justify-center">
                  <Megaphone className="w-7 h-7 text-[#C5A059]" />
                </div>
                <div>
                  <p className="text-xs font-bold text-[#C5A059]/80 uppercase tracking-[0.2em]">Crescimento</p>
                  <h3 className="text-2xl font-extrabold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>O Megafone</h3>
                </div>
              </div>

              <p className="text-base text-slate-200/90 leading-relaxed mb-6">
                <strong className="text-white">Elogios são convidados automaticamente a postar no Google Maps.</strong> Sua nota sobe organicamente e atrai novos clientes diariamente.
              </p>

              <div className="pt-6 border-t border-white/10">
                <DashboardMockup />
                <p className="text-[11px] text-white/40 text-center mt-3">
                  Dashboard com o ranking dos problemas resolvidos
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== PROVA VISUAL ==================== */}
      <section className="py-20 lg:py-24 bg-[#FAFBFC]" data-testid="visual-proof-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-[#C5A059] uppercase tracking-[0.2em] mb-4">Quem já usa</p>
            <h2
              className="text-3xl sm:text-4xl font-extrabold text-[#0A2647] tracking-tight mb-4"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Negócios que blindaram sua nota no <span className="text-[#C5A059]">Google Maps</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { img: IMAGES.padaria, label: 'Padarias & Supermercados', desc: 'Acompanhe a qualidade dos produtos e o atendimento no balcão em tempo real.', featured: true },
              { img: IMAGES.restaurant, label: 'Restaurantes & Bares', desc: 'Resolva reclamações antes que virem avaliações negativas no Google.' },
              { img: IMAGES.cafe, label: 'Cafeterias & Confeitarias', desc: 'Saiba se o cafe e os doces estao agradando — direto de quem consome.' },
              { img: IMAGES.hotel, label: 'Hoteis & Pousadas', desc: 'Meca a satisfação dos hospedes na recepção e melhore a experiência.' },
              { img: IMAGES.dental, label: 'Clínicas & Consultórios', desc: 'Reduza o tempo de espera e aumente as avaliações positivas no Google.' },
              { img: IMAGES.fashion, label: 'Lojas & Boutiques', desc: 'Saiba como sua equipe atende no provador e no caixa.' },
            ].map((item) => (
              <div
                key={item.label}
                className={`group rounded-2xl overflow-hidden bg-white border transition-all duration-400 ${
                  item.featured
                    ? 'shadow-[0_8px_40px_rgba(197,160,89,0.15)] border-[#C5A059]/30 sm:col-span-2 lg:col-span-1'
                    : 'shadow-[0_2px_20px_rgba(0,0,0,0.04)] border-slate-100 hover:shadow-[0_12px_48px_rgba(10,38,71,0.12)] hover:border-[#C5A059]/20'
                }`}
                data-testid={`visual-proof-${item.label}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img src={item.img} alt={item.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A2647]/50 to-transparent" />
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-[#0A2647] text-lg mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>{item.label}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== SEGMENTOS ==================== */}
      <section className="py-16 lg:py-20 bg-white" data-testid="segments-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-[#C5A059] uppercase tracking-[0.2em] mb-4">Segmentos</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0A2647] tracking-tight mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Feito para <span className="text-[#C5A059]">qualquer negócio que atende presencialmente</span>
            </h2>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {segments.map((seg) => (
              <div
                key={seg.name}
                className="flex flex-col items-center gap-3 p-4 bg-white rounded-xl border border-slate-100 shadow-[0_1px_8px_rgba(0,0,0,0.03)] hover:border-[#C5A059]/40 hover:shadow-[0_4px_20px_rgba(197,160,89,0.12)] transition-all duration-300 group cursor-default"
                data-testid={`segment-${seg.name}`}
              >
                <div className="w-12 h-12 bg-[#0A2647] text-[#C5A059] rounded-xl flex items-center justify-center group-hover:bg-[#C5A059] group-hover:text-white transition-colors duration-300">
                  {seg.icon}
                </div>
                <span className="text-xs font-semibold text-slate-700 text-center leading-tight">{seg.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FUNCIONALIDADES ==================== */}
      <section className="py-20 lg:py-24 bg-[#FAFBFC]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-[#C5A059] uppercase tracking-[0.2em] mb-4">Funcionalidades</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A2647] tracking-tight mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Tudo que você precisa em <span className="text-[#C5A059]">um só lugar</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: <Share2 className="w-6 h-6" />, title: 'Métrica exclusiva: Taxa de Recomendação Pública', desc: 'Métrica exclusiva do Falo com o Dono que mostra quanto da sua reputação está virando avaliação pública no Google. Diferente do NPS, mede a manifestação espontânea dos clientes que escolheram falar.', highlight: true },
              { icon: <QrCode className="w-6 h-6" />, title: 'QR Code no Balcão, Mesa ou Recepção', desc: 'Gere QR Codes exclusivos e Placas premium prontas para imprimir. Funciona em qualquer ponto de contato.' },
              { icon: <BarChart3 className="w-6 h-6" />, title: 'Dashboard Inteligente', desc: 'KPIs, graficos, ranking comparativo semanal e alertas de crise. Tudo em tempo real.' },
              { icon: <Camera className="w-6 h-6" />, title: 'Anexo de Fotos (Exclusivo)', desc: 'Receba provas visuais: seus clientes podem anexar fotos de produtos ou do ambiente diretamente na avaliação. Recurso exclusivo!', highlight: true },
              { icon: <MessageCircle className="w-6 h-6" />, title: 'Respostas via WhatsApp', desc: 'Responda avaliações com um clique direto pelo WhatsApp. Ação rápida e pessoal.' },
              { icon: <TrendingUp className="w-6 h-6" />, title: 'Relatórios PDF e Excel', desc: 'Exporte relatórios completos para reunioes, análises e tomada de decisão estratégica.' },
              { icon: <Smartphone className="w-6 h-6" />, title: 'Personalização Total', desc: 'URL personalizada, logo própria e cores da sua marca. Seu cliente ve a sua identidade, não a nossa.' },
            ].map((feat) => (
              <div
                key={feat.title}
                className={`group relative bg-white rounded-2xl p-7 border transition-all duration-300 ${
                  feat.highlight
                    ? 'border-[#C5A059]/30 shadow-[0_4px_24px_rgba(197,160,89,0.12)] ring-1 ring-[#C5A059]/20'
                    : 'border-slate-100 shadow-[0_2px_16px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_32px_rgba(10,38,71,0.08)] hover:border-[#C5A059]/20'
                }`}
                data-testid={`feature-${feat.title}`}
              >
                {feat.highlight && (
                  <div className="absolute -top-2.5 left-6">
                    <span className="bg-[#C5A059] text-[#0A2647] px-3 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">Novo</span>
                  </div>
                )}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${
                  feat.highlight
                    ? 'bg-[#C5A059] text-white'
                    : 'bg-[#0A2647] text-[#C5A059] group-hover:bg-[#C5A059] group-hover:text-white'
                } transition-colors duration-300`}>
                  {feat.icon}
                </div>
                <h3 className="text-base font-bold text-[#0A2647] mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>{feat.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════ AIDA 3: ACAO - 3 Passos (Blindada em 5 minutos) ══════════════════ */}
      <section className="relative py-20 lg:py-28 overflow-hidden" data-testid="five-minutes-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#071c38] via-[#0A2647] to-[#071c38]" />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14 lg:mb-16">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5 mb-5">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs text-emerald-400 font-bold uppercase tracking-wider">Zero Burocracia</span>
            </div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-[1.1] tracking-tight mb-5"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
              data-testid="five-minutes-title"
            >
              Sua empresa blindada em apenas <span className="text-[#C5A059]">5 minutos</span>
            </h2>
            <p className="text-base sm:text-lg text-slate-300/80 max-w-2xl mx-auto leading-relaxed">
              Nada de equipe técnica, integração complexa ou manual de 80 páginas. Três passos e você está no ar.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {[
              {
                num: '01',
                icon: <UserPlus className="w-8 h-8" />,
                title: 'Cadastro (1 min)',
                desc: 'Crie sua conta sem precisar de equipe técnica.',
              },
              {
                num: '02',
                icon: <QrCodeIcon className="w-8 h-8" />,
                title: 'Atrito Zero',
                desc: 'O cliente não baixa nada. Ele apenas aponta a câmera para o QR Code na mesa ou balcão.',
              },
              {
                num: '03',
                icon: <Bell className="w-8 h-8" />,
                title: 'Gestão Visual',
                desc: 'Você recebe alertas imediatos (Vermelho, Laranja ou Verde). Simples e feito para quem não tem tempo a perder.',
              },
            ].map((step, idx) => (
              <div
                key={step.num}
                className="relative bg-white/[0.04] backdrop-blur-sm rounded-2xl p-7 lg:p-8 border border-white/10 hover:border-[#C5A059]/30 hover:bg-white/[0.06] transition-all duration-400"
                data-testid={`five-min-step-${step.num}`}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-14 h-14 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-2xl flex items-center justify-center text-[#C5A059]">
                    {step.icon}
                  </div>
                  <span className="text-4xl font-extrabold text-[#C5A059]/20 leading-none" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {step.num}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {step.title}
                </h3>
                <p className="text-sm text-slate-300/80 leading-relaxed">{step.desc}</p>

                {idx < 2 && (
                  <div className="hidden md:flex absolute top-1/2 -right-5 -translate-y-1/2 w-10 h-10 items-center justify-center z-10">
                    <ArrowRight className="w-5 h-5 text-[#C5A059]/40" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== DEPOIMENTOS ==================== */}
      <section className="py-20 lg:py-24 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-[#C5A059] uppercase tracking-[0.2em] mb-4">Depoimentos</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A2647] tracking-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              O que dizem nossos clientes
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {testimonials.map((t) => (
              <div
                key={t.name}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-[0_2px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_40px_rgba(10,38,71,0.08)] transition-all duration-300"
                data-testid={`testimonial-${t.name}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => <GoldStar key={`review-star-${i}`} size="w-4 h-4" />)}
                  </div>
                  {t.tag && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#C5A059]/10 text-[#C5A059] border border-[#C5A059]/20">{t.tag}</span>
                  )}
                </div>
                <p className="text-slate-600 text-sm leading-relaxed mb-6 italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#0A2647] rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-[#C5A059]">{t.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#0A2647]">{t.name}</p>
                    <p className="text-xs text-slate-400">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════ FECHAMENTO: Pare de perder dinheiro no escuro ══════════════════ */}
      <section className="relative py-16 lg:py-20 bg-gradient-to-b from-white to-[#FAFBFC]" data-testid="closing-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-[#0A2647]/5 border border-[#0A2647]/10 rounded-full px-4 py-1.5 mb-6">
            <AlertCircle className="w-3.5 h-3.5 text-[#0A2647]" />
            <span className="text-xs text-[#0A2647] font-bold uppercase tracking-wider">Última dose de realidade</span>
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0A2647] tracking-tight leading-[1.1] mb-6"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
            data-testid="closing-title"
          >
            Pare de perder dinheiro <span className="text-[#C5A059]">no escuro</span>
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto" data-testid="closing-text">
            O Falo Com O Dono custa menos que uma pizza por mês. Se o sistema salvar apenas <strong className="text-[#0A2647]">1 cliente insatisfeito</strong>, ele já se paga com folga. Comece hoje com <strong className="text-[#0A2647]">risco zero</strong>.
          </p>
        </div>
      </section>

      {/* ══════════════════ CALCULADORA DE ROI - Cria a dor antes do preço ══════════════════ */}
      <section id="roi-section" className="py-14 lg:py-20 bg-slate-100" data-testid="roi-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <RoiCalculator
            ctaText="Quero parar de perder dinheiro"
            ctaHref="#pricing-section"
            variant="light"
          />
        </div>
      </section>

      {/* ══════════════════ AIDA 4: CONVERSAO - Planos ══════════════════ */}
      <section id="pricing-section" className="py-16 lg:py-24 bg-[#0A2647] relative overflow-hidden" data-testid="pricing-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#C5A059]/[0.04] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#C5A059]/[0.03] rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14 lg:mb-16">
            <p className="text-sm font-semibold text-[#C5A059] uppercase tracking-[0.2em] mb-4">Investimento</p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-5"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Planos e Preços
            </h2>
            <p className="text-base text-slate-400 max-w-lg mx-auto">
              Escolha o plano ideal e comece a transformar seu atendimento hoje.
            </p>
          </div>

          <div className={`grid sm:grid-cols-2 gap-3 lg:gap-4 max-w-7xl mx-auto ${plans.length <= 3 ? 'lg:grid-cols-3 lg:max-w-5xl' : 'lg:grid-cols-6'}`}>
            {plans.map((plan) => {
              const isHighlighted = plan.best || plan.complete;
              return (
              <div
                key={plan.planId || plan.name}
                className={`rounded-2xl p-6 transition-all duration-300 relative flex flex-col ${
                  isHighlighted
                    ? 'bg-white text-slate-900 shadow-[0_20px_60px_rgba(0,0,0,0.3)] ring-2 ring-[#C5A059]'
                    : plan.isFree
                    ? 'bg-white/[0.04] backdrop-blur-sm text-white border border-white/10 hover:border-[#C5A059]/30 hover:bg-white/[0.06]'
                    : 'bg-white/[0.08] backdrop-blur-sm text-white border border-white/10 hover:border-[#C5A059]/30 hover:bg-white/[0.1]'
                }`}
                data-testid={`plan-card-${plan.planId || plan.name}`}
              >
                {plan.best && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-[#C5A059] text-[#0A2647] px-4 py-1 rounded-full text-xs font-bold shadow-lg whitespace-nowrap">
                      MAIS POPULAR
                    </span>
                  </div>
                )}
                {plan.complete && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-[#0A2647] text-[#C5A059] px-4 py-1 rounded-full text-xs font-bold shadow-lg whitespace-nowrap border border-[#C5A059]/40">
                      MAIS COMPLETO
                    </span>
                  </div>
                )}
                <h3 className="text-base font-bold mb-1 text-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>{plan.name}</h3>
                {plan.seal && (
                  <p className={`text-center text-[10px] font-semibold uppercase tracking-wider mb-2 ${isHighlighted ? 'text-[#C5A059]' : 'text-[#C5A059]/80'}`}>{plan.seal}</p>
                )}
                {!plan.isFree && (
                  <div className="text-center mb-2">
                    <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-emerald-500 text-white">Assine Agora</span>
                  </div>
                )}
                <div className="text-center mb-4 mt-1">
                  {plan.isFree ? (
                    <span className="text-3xl font-extrabold text-[#C5A059]" style={{ fontFamily: 'Montserrat, sans-serif' }}>Grátis</span>
                  ) : (
                    <>
                      <span className="text-3xl font-extrabold" style={{ fontFamily: 'Montserrat, sans-serif' }}>R$ {plan.price}</span>
                      <span className={isHighlighted ? 'text-slate-500 text-xs' : 'text-slate-400 text-xs'}>/mes</span>
                    </>
                  )}
                </div>
                {plan.upgrade && (
                  <p className={`text-center text-[11px] font-medium mb-3 pb-3 border-b ${isHighlighted ? 'text-slate-500 border-slate-200' : 'text-slate-400 border-white/10'}`}>
                    {plan.upgrade}
                  </p>
                )}
                {plan.freeBadge && (
                  <div className="mb-3 py-2 px-3 rounded-lg bg-[#C5A059]/10 border border-[#C5A059]/30 text-center">
                    <p className="text-[10px] font-bold text-[#C5A059] uppercase tracking-wider">Inclui Automação de Reputação no Google</p>
                  </div>
                )}
                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs">
                      <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#C5A059]" />
                      <span className={isHighlighted ? 'text-slate-600' : 'text-slate-300'}>{f}</span>
                    </li>
                  ))}
                  {plan.googleFeature && (
                    <li className="flex items-start gap-2 text-xs">
                      <Star className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#C5A059] fill-[#C5A059]" />
                      <span className={`font-bold ${isHighlighted ? 'text-[#0A2647]' : 'text-[#C5A059]'}`}>{plan.googleFeature}</span>
                    </li>
                  )}
                  {['free', 'básico', 'pro', 'enterprise', 'elite', 'five_stars'].includes(plan.planId) && (
                    <li className="flex items-start gap-2 text-xs" data-testid={`wa-highlight-${plan.planId}`}>
                      <Phone className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-emerald-400" />
                      <span className={`font-bold ${isHighlighted ? 'text-emerald-700' : 'text-emerald-400'}`}>Alerta de Crise + Resumo Semanal via WhatsApp</span>
                    </li>
                  )}
                </ul>
                <Link to={plan.isFree ? `/register${checkoutQS}` : `/checkout/${plan.planId}${checkoutQS}`}>
                  <Button
                    className="w-full h-10 font-bold rounded-xl text-xs transition-all duration-300 bg-[#C5A059] hover:bg-[#d4af61] text-[#0A2647] hover:shadow-[0_0_24px_rgba(197,160,89,0.3)]"
                    data-testid={`plan-cta-${plan.planId || plan.name}`}
                  >
                    {plan.isFree ? 'Ativar Agora' : 'Assinar Plano'}
                  </Button>
                </Link>
                {plan.isFree && (
                  <p className="text-[10px] text-center mt-2 text-slate-400 leading-snug" data-testid="free-microcopy">
                    Sem cartão de crédito. Comece a ouvir seus clientes em 5 minutos.
                  </p>
                )}
              </div>
            )})}
          </div>

          <div className="text-center mt-10" data-testid="whatsapp-consultant">
            <a
              href="https://wa.me/5531991316297"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#20BD5A] text-white font-bold px-8 py-4 rounded-2xl text-sm transition-all duration-300 hover:shadow-[0_8px_32px_rgba(37,211,102,0.35)] active:scale-[0.98]"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Dúvidas sobre o plano ideal? Fale com nosso Consultor
            </a>
          </div>

          <p className="text-center text-xs text-slate-500 mt-6">Todo cadastro novo começa com 30 dias grátis no plano Empresarial. O 1º boleto só vence no 30º dia. Cancele quando quiser.</p>
        </div>
      </section>

      {/* ==================== CTA FINAL ==================== */}
      <section className="py-20 lg:py-24 bg-white relative overflow-hidden" data-testid="final-cta">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#C5A059]/[0.03] rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="flex justify-center mb-6">
            {[...Array(5)].map((_, i) => <GoldStar key={`cta-star-${i}`} size="w-7 h-7 sm:w-8 sm:h-8" />)}
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0A2647] tracking-tight mb-5"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Pronto para blindar sua reputação?
          </h2>
          <p className="text-base sm:text-lg text-slate-500 mb-10 max-w-xl mx-auto leading-relaxed">
            Cada dia sem o Falo Com O Dono é um dia de clientes saindo calados — e reclamando em outro lugar.
          </p>
          <Link to="/feira">
            <Button
              size="lg"
              className="relative bg-[#C5A059] hover:bg-[#d4af61] text-[#0A2647] font-bold text-base px-10 h-14 rounded-xl gap-2 transition-all duration-300 hover:shadow-[0_0_40px_rgba(197,160,89,0.45)] active:scale-[0.98]"
              data-testid="final-cta-btn"
            >
              ⚡ Implantar Agora
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
          <p className="text-xs text-slate-400 mt-5">Grátis para começar. Sem cartão de crédito. Setup em 5 minutos.</p>
        </div>
      </section>

      {/* ==================== PERGUNTAS FREQUENTES ==================== */}
      <PublicFaq />

      {/* ==================== FOOTER ==================== */}
      <footer className="bg-[#0A2647] text-slate-400 py-8 border-t border-white/5" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <img src={LOGO_URL} alt="Logo" className="h-10 w-auto object-contain" />
              <div>
                <span className="font-bold text-base text-white block" style={{ fontFamily: 'Montserrat, sans-serif' }}>Falo Com O Dono<sup className="text-[10px] text-[#C5A059] font-bold ml-0.5">™</sup></span>
                <a href="mailto:falocomodono@gmail.com" className="text-xs text-[#C5A059] hover:underline" data-testid="footer-email">falocomodono@gmail.com</a>
              </div>
            </div>
            <div className="flex flex-col items-center md:items-end gap-1.5">
              <a href="/afiliados" className="text-sm font-semibold text-[#C5A059] hover:text-[#C5A059]/80 transition-colors" data-testid="footer-affiliate-link">
                Seja um Afiliado
              </a>
              <a href="/termos" className="text-xs text-slate-300 hover:text-white transition-colors" data-testid="footer-terms-link">
                Termos de Uso e Privacidade
              </a>
              <p className="text-xs text-slate-400">
                &copy; 2026 FaloComODono&trade;. Todos os direitos reservados.
              </p>
              <p className="text-[10px] text-slate-500">
                Marca requerida e protegida junto ao INPI.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
